import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './curve-theme.css';

export type CaseStatusItem = {
  id: string;
  label: string;
};

const STATUS_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  pending: {
    bg: 'var(--curve-status-pending-bg, #FFF7ED)',
    color: 'var(--curve-status-pending-text, #EA580C)',
    border: 'var(--curve-status-pending-border, #FDBA74)',
  },
  in_progress: {
    bg: 'var(--curve-status-inprog-bg, #EFF6FF)',
    color: 'var(--curve-status-inprog-text, #2563EB)',
    border: 'var(--curve-status-inprog-border, #93C5FD)',
  },
  closed: {
    bg: 'var(--curve-status-closed-bg, #F0FDF4)',
    color: 'var(--curve-status-closed-text, #16A34A)',
    border: 'var(--curve-primary-border, #86EFAC)',
  },
};

type CaseStatusDropdownProps = {
  items: CaseStatusItem[];
  selectedId: string;
  onChange: (id: string) => void;
  disabled?: boolean;
};

export function CaseStatusDropdown({
  items,
  selectedId,
  onChange,
  disabled = false,
}: CaseStatusDropdownProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.id === selectedId) ?? items[0];
  const style = STATUS_STYLE[selectedId] ?? {
    bg: 'var(--curve-bg-gray, #F5F5F5)',
    color: 'var(--curve-text-muted, #64748B)',
    border: 'var(--curve-border, #E5E5E5)',
  };

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={disabled ? undefined : () => setOpen((v) => !v)}
        className="curve-btn"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 10px',
          borderRadius: 'var(--curve-radius-pill, 9999px)',
          border: `1px solid ${style.border}`,
          background: style.bg,
          color: style.color,
          fontSize: 'var(--curve-text-xs, 12px)',
          fontWeight: 600,
          cursor: disabled ? 'default' : 'pointer',
          whiteSpace: 'nowrap',
          transition: 'opacity var(--curve-transition)',
        }}
        onMouseEnter={disabled ? undefined : (e) => {
          (e.currentTarget as HTMLButtonElement).style.opacity = '0.85';
        }}
        onMouseLeave={disabled ? undefined : (e) => {
          (e.currentTarget as HTMLButtonElement).style.opacity = '1';
        }}
      >
        {selected?.label ?? t('CaseStatus.placeholder')}
        {!disabled && <span style={{ fontSize: '9px', opacity: 0.8 }}>▼</span>}
      </button>

      {!disabled && open && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 100 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              background: 'var(--curve-bg-white, #FFFFFF)',
              border: '1px solid var(--curve-border, #E5E5E5)',
              borderRadius: 'var(--curve-radius-md, 8px)',
              boxShadow: 'var(--curve-shadow-md, 0 4px 12px rgba(0,0,0,0.10))',
              zIndex: 101,
              minWidth: '110px',
              overflow: 'hidden',
            }}
          >
            {items.map((item) => {
              const s = STATUS_STYLE[item.id];
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onChange(item.id);
                    setOpen(false);
                  }}
                  className="curve-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: 'var(--curve-text-small, 13px)',
                    color: s
                      ? s.color
                      : 'var(--curve-text-body, #334155)',
                    fontWeight: item.id === selectedId ? 600 : 400,
                    background: item.id === selectedId
                      ? (s?.bg ?? 'var(--curve-primary-soft, #F0FDF4)')
                      : 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (item.id !== selectedId)
                      (e.currentTarget as HTMLButtonElement).style.background =
                        'var(--curve-bg-gray, #F5F5F5)';
                  }}
                  onMouseLeave={(e) => {
                    if (item.id !== selectedId)
                      (e.currentTarget as HTMLButtonElement).style.background =
                        'transparent';
                  }}
                >
                  {s && (
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: s.color,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {item.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
